export enum ContractStatus {
  ACTIVE = 'active',
  SIGNED = 'signed',
  TERMINATED = 'terminated',
  EXPIRED = 'expired',
}

export enum ContractType {
  RENTAL = 'rental',
  RIGHT_OF_OCCUPANCY = 'right_of_occupancy',
}

export enum ResidentUserRegistrationStatus {
  NOT_STARTED = 'not_started',
  PENDING_EMAIL_VERIFICATION = 'pending_email_verification',
  COMPLETE = 'complete',
}

export enum ResidentUserAuthLevel {
  WEAK = 'weak',
  STRONG = 'strong',
}

export const ParkingSpotEquipments = [
  'yard',
  'covered',
  'ev_charger',
  'garage',
  'heating_outlet',
  'motorbike_spot',
  'building',
  'disabled_spot',
] as const

export type ParkingSpotEquipment = (typeof ParkingSpotEquipments)[number]

export enum ResidentNotificationCategory {
  normal = 'normal',
  arrear = 'arrear',
  contract_lifecycle = 'contract_lifecycle',
  contract_termination_request_approval_needed = 'contract_termination_request_approval_needed',
  contract_termination_request_created = 'contract_termination_request_created',
  contract_termination_request_kenno_accepted = 'contract_termination_request_kenno_accepted',
  contract_termination_request_kenno_rejected = 'contract_termination_request_kenno_rejected',
  contract_termination_request_tenant_accepted = 'contract_termination_request_tenant_accepted',
  contract_termination_request_tenant_rejected = 'contract_termination_request_tenant_rejected',
  message = 'message',
  contract_modification_request_approval_needed = 'contract_modification_request_approval_needed',
  contract_modification_request_created = 'contract_modification_request_created',
  contract_modification_request_kenno_accepted = 'contract_modification_request_kenno_accepted',
  contract_modification_request_kenno_rejected = 'contract_modification_request_kenno_rejected',
  contract_modification_request_tenant_accepted = 'contract_modification_request_tenant_accepted',
  contract_modification_request_tenant_rejected = 'contract_modification_request_tenant_rejected',
}

export enum ResidentNotificationPriority {
  normal = 'normal',
  important = 'important',
}

export enum ContractTerminationReason {
  /** Asunnon koko */
  APARTMENT_SIZE = 'apartment_size',
  APARTMENT_LOCATION = 'apartment_location',
  APARTMENT_TEMPERATURE = 'apartment_temperature',
  APARTMENT_DOES_NOT_MEET_NEEDS = 'apartment_does_not_meet_needs',
  /** Alueen huonot palvelut */
  AREA_POOR_SERVICES = 'area_poor_services',
  AREA_DISTURBANCES = 'area_disturbances',
  /** Häiritsevät naapurit / häiriö talossa */
  DISTURBING_NEIGHBOURS_OR_BUILDING = 'disturbing_neighbours_or_building',
  EVICTION = 'eviction',
  /** Perhekoon muutos (ero, avio/avoliitto, lapsi) */
  FAMILY_SIZE_CHANGE = 'family_size_change',
  BUSINESS_CLOSURE = 'business_closure',
  BUSINESS_CLOSURE_IN_MUNICIPALITY = 'business_closure_in_municipality',
  BUSINESS_STRUCTURE_CHANGE = 'business_structure_change',
  HEALTH_REASONS = 'health_reasons',
  /** Vastikkeen suuruus */
  HOUSING_COMPANY_FEE_AMOUNT = 'housing_company_fee_amount',
  /** Sisäinen vaihto */
  INTERNAL_SWAP = 'internal_swap',
  /** Muutto työn tai opiskelun vuoksi */
  MOVE_DUE_WORK = 'move_due_work',
  /** Paikkakunnan vaihto */
  MOVE_TO_ANOTHER_MUNICIPALITY = 'move_to_another_municipality',
  MOVE_TO_MORE_AFFORDABLE_BUSINESS_PREMISES_IN_MUNICIPALITY = 'move_to_more_affordable_business_premises_in_municipality',
  /** Muu syy */
  OTHER = 'other',
  /** Asunnon huono kunto */
  POOR_APARTMENT_CONDITION = 'poor_apartment_condition',
  /** Asunnon varusteet ja/tai ominaisuudet */
  POOR_APARTMENT_EQUIPMENT_OR_PROPERTIES = 'poor_apartment_equipment_or_properties',
  /** Kiinteistön huono kunto */
  POOR_PROPERTY_CONDITION = 'poor_property_condition',
  /** Kiinteistön huono hoito */
  POOR_PROPERTY_MAINTENANCE = 'poor_property_maintenance',
  /** Huonot liikenneyhteydet */
  POOR_TRANSPORTATION_OPTIONS = 'poor_transportation_options',
  /** Asumiskustannukset */
  HOUSING_COSTS = 'housing_costs',
  RENOVATION = 'renovation',
  RIGHT_OF_OCCUPANCY_ENDS = 'right_of_occupancy_ends',
  SPACE_NEEDS_CHANGE = 'space_needs_change',
  STUDIES_END = 'studies_end',
  /** Kuolema */
  DEATH = 'death',
  UNKNOWN = 'unknown',
}

export enum LegacyContractTerminationReason {
  BUYING_PROPERTY = 'buying_property',
  MOVE_TO_ANOTHER_APARTMENT_OF_SAME_OWNER = 'move_to_another_apartment_of_same_owner',
  MOVE_TO_ANOTHER_RENTAL = 'move_to_another_rental',
  MOVE_TO_CARE_HOME_OR_HOSPITAL = 'move_to_care_home_or_hospital',
  MOVE_TO_RIGHT_OF_OCCUPANCY = 'move_to_right_of_occupancy',
}

export enum ContractTerminationDestination {
  BUYING_PROPERTY = 'buying_property',
  MOVE_TO_ANOTHER_APARTMENT_OF_SAME_OWNER = 'move_to_another_apartment_of_same_owner',
  MOVE_TO_ANOTHER_RENTAL = 'move_to_another_rental',
  MOVE_TO_RIGHT_OF_OCCUPANCY_OF_SAME_OWNER = 'move_to_right_of_occupancy_of_same_owner',
  MOVE_TO_RIGHT_OF_OCCUPANCY_OF_ANOTHER_OPERATOR = 'move_to_right_of_occupancy_of_another_operator',
  MOVE_TO_RIGHT_OF_OCCUPANCY = 'move_to_right_of_occupancy',
  MOVE_TO_CARE_HOME_OR_HOSPITAL = 'move_to_care_home_or_hospital',
  OTHER = 'other',
  UNKNOWN = 'unknown',
}

export type Id = number

export enum TenantEventStatus {
  APPROVED = 'approved',
  PENDING_TENANT_APPROVAL = 'pending_tenant_approval',
  READY_FOR_REVIEW = 'ready_for_review',
  REJECTED = 'rejected',
}

export enum TerminationDisallowedReason {
  CONTRACT_TERMINATED = 'contract_terminated', // sopimus on jo irtisanottu
  PENDING_TERMINATION_TENANT_APPROVALS = 'pending_termination_tenant_approvals', // irtisanomisilmoitus: kaikki asukkaat eivät ole hyväksyneet
  PENDING_TERMINATION_REVIEW = 'pending_termination_review', // pendaava irtisanomisilmoitus: käsittelemättä kiinteistöyhtiöryhmän puolella
  IS_ESTATE_CONTRACT = 'is_estate_contract', // kyseessä on kuolinpesä
  HAS_GUARDIAN_OF_INTERESTS = 'has_guardian_of_interests', // jollain sopijaosapuolella on edunvalvoja
  MISSING_RESIDENT_USERS = 'missing_resident_users', // kaikilla sopijoilla ei ole asukassivutunnusta
  CONTRACT_TAG = 'contract_tag', // sopimuksella on estävä tagi
  NOT_CONTRACT_PARTY = 'not_contract_party', // käyttäjä ei ole sopijaosapuoli
  TENANTS_WITH_UNDETERMINED_AGE = 'tenants_with_undetermined_age', // sopimuksella on asukkaita, joiden ikä ei ole tiedossa
  HAS_COMPANY_TENANT = 'has_company_tenant', // sopimuksella on yritysosapuoli
  PENDING_MODIFICATION_REQUEST = 'pending_modification_request', // avoinna oleva sopimmusmuutosehdotus
  NOT_STARTED_YET = 'not_started_yet', // sopimus ei ole vielä alkanut
  OPEN_CONTINUATION_OFFER = 'open_continuation_offer', // avoinna oleva jatkotarjous
  PENDING_CONTRACT_RESIGN = 'pending_contract_resign', // Sopimuksen uudelleenallekirjoitus on kesken
  CONTRACT_EXPIRED = 'contract_expired',
  TENANT_MOVED_OUT = 'tenant_moved_out',
}

export enum ResidentAppErrorCode {
  // Generic
  UNAUTHORIZED = 'UNAUTHORIZED',
  BAD_REQUEST = 'BAD_REQUEST',
  NOT_FOUND = 'NOT_FOUND',
  CONFLICT = 'CONFLICT',
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
  GATEWAY_TIMEOUT = 'GATEWAY_TIMEOUT',
  CORE_TIMEOUT = 'CORE_TIMEOUT',
  UPSTREAM_TIMEOUT = 'UPSTREAM_TIMEOUT',
  REDIRECT = 'REDIRECT',

  UNKNOWN = 'UNKNOWN',
  UNEXPECTED = 'UNEXPECTED',

  // Stale client: cached frontend bundle submitted a value that no longer exists
  STALE_CLIENT_REFRESH_REQUIRED = 'STALE_CLIENT_REFRESH_REQUIRED',

  // Reservation validation
  RESERVATION_END_BEFORE_START = 'RESERVATION_END_BEFORE_START',
  CONTRACT_END_BEFORE_RESERVATION_START = 'CONTRACT_END_BEFORE_RESERVATION_START',
  RESERVATION_START_BEFORE_CONTRACT_START = 'RESERVATION_START_BEFORE_CONTRACT_START',
  RESERVATION_OPEN_ENDED_NOT_ALLOWED = 'RESERVATION_OPEN_ENDED_NOT_ALLOWED',
  RESERVATION_END_AFTER_CONTRACT_END = 'RESERVATION_END_AFTER_CONTRACT_END',
  RESERVATION_END_BEFORE_TODAY = 'RESERVATION_END_BEFORE_TODAY',
  RECURRING_RESERVATION_ALREADY_STARTED = 'RECURRING_RESERVATION_ALREADY_STARTED',
  RECURRING_RESERVATION_TERMINATION_TODAY_NOT_ALLOWED = 'RECURRING_RESERVATION_TERMINATION_TODAY_NOT_ALLOWED',
  RESERVATION_ON_STARTED_SLOTS_NOT_ALLOWED = 'RESERVATION_ON_STARTED_SLOTS_NOT_ALLOWED',
  RESERVATION_MAX_RESERVED_SLOTS_PER_WEEK = 'RESERVATION_MAX_RESERVED_SLOTS_PER_WEEK',
  RESERVATION_INVALID_SLOT_GRID_POSITION = 'RESERVATION_INVALID_SLOT_GRID_POSITION',

  // Validation
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  INVALID_RESPONSE = 'INVALID_RESPONSE',

  // Contracts
  NO_ACTIVE_CONTRACTS = 'NO_ACTIVE_CONTRACTS',
  ERROR_FETCHING_CONTRACTS = 'ERROR_FETCHING_CONTRACTS',

  // Contract termination
  TERMINATE_CONTRACT_INIT_CONFIRM_ERROR = 'TERMINATE_CONTRACT_INIT_CONFIRM_ERROR',
  TERMINATE_CONTRACT_CONFIRM_ERROR = 'TERMINATE_CONTRACT_CONFIRM_ERROR',
  TERMINATE_CONTRACT_REQUEST_UNAVAILABLE = 'TERMINATE_CONTRACT_REQUEST_UNAVAILABLE',
  TERMINATE_CONTRACT_MISSING_RESIDENT_CREDENTIALS = 'TERMINATE_CONTRACT_MISSING_RESIDENT_CREDENTIALS',
  TERMINATE_CONTRACT_PENDING_REQUEST = 'TERMINATE_CONTRACT_PENDING_REQUEST',

  // Tenant addition
  ADD_TENANT_INIT_CONFIRM_ERROR = 'ADD_TENANT_INIT_CONFIRM_ERROR',

  // Contract modification
  MODIFICATION_CONTRACT_INIT_CONFIRM_ERROR = 'MODIFICATION_CONTRACT_INIT_CONFIRM_ERROR',
  MODIFICATION_CONTRACT_CONFIRM_ERROR = 'MODIFICATION_CONTRACT_CONFIRM_ERROR',

  // Confirm operation
  CONFIRM_OPERATION_CANCELLED = 'CONFIRM_OPERATION_CANCELLED',
  CONFIRM_OPERATION_NO_IDENTITY_VERIFIER = 'CONFIRM_OPERATION_NO_IDENTITY_VERIFIER',
  CONFIRM_OPERATION_NO_IDENTITY = 'CONFIRM_OPERATION_NO_IDENTITY',
  CONFIRM_OPERATION_NO_USER = 'CONFIRM_OPERATION_NO_USER',
  CONFIRM_OPERATION_NO_ATTRIBUTES = 'CONFIRM_OPERATION_NO_ATTRIBUTES',
  CONFIRM_OPERATION_IDENTITY_MISMATCH = 'CONFIRM_OPERATION_IDENTITY_MISMATCH',
  CONFIRM_OPERATION_GENERIC_ERROR = 'CONFIRM_OPERATION_GENERIC_ERROR',
  CONFIRM_OPERATION_TERMINATION_DATE_INVALID = 'CONFIRM_OPERATION_TERMINATION_DATE_INVALID',
  CONFIRM_OPERATION_INVALID_CONFIRM_TYPE = 'CONFIRM_OPERATION_INVALID_CONFIRM_TYPE',

  // Login
  LOGIN_FAILED = 'LOGIN_FAILED',
  LOGIN_EXPIRED = 'LOGIN_EXPIRED',
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  USER_NOT_CONFIRMED = 'USER_NOT_CONFIRMED',

  // Auth
  AUTH_ERROR = 'AUTH_ERROR',
  ERROR_UNBOUND_RESIDENT_USER = 'ERROR_UNBOUND_RESIDENT_USER',
  IDENTIFICATION_REQUIRED = 'IDENTIFICATION_REQUIRED',
  FEATURE_ACCESS_DENIED = 'FEATURE_ACCESS_DENIED',
  MOVED_OUT_WRITE_ACCESS_DENIED = 'MOVED_OUT_WRITE_ACCESS_DENIED',

  // Forgot password
  FORGOT_OR_CHANGE_PASSWORD = 'FORGOT_OR_CHANGE_PASSWORD',
  SEND_PASSWORD_RESET_CODE = 'SEND_PASSWORD_RESET_CODE',

  // Defect notice
  DEFECT_NOTICE_SUBMIT_ERROR = 'DEFECT_NOTICE_SUBMIT_ERROR',
  DEFECT_NOTICE_MISSING_PHONE_NUMBER = 'DEFECT_NOTICE_MISSING_PHONE_NUMBER',

  // Question
  QUESTION_SUBMIT_ERROR = 'QUESTION_SUBMIT_ERROR',
  QUESTION_MISSING_PHONE_NUMBER = 'QUESTION_MISSING_PHONE_NUMBER',

  // Feedback
  FEEDBACK_SUBMIT_ERROR = 'FEEDBACK_SUBMIT_ERROR',
  FEEDBACK_MISSING_PHONE_NUMBER = 'FEEDBACK_MISSING_PHONE_NUMBER',

  // Documents / Attachments
  FILE_FAILED_TO_OPEN = 'FILE_FAILED_TO_OPEN',
  FILE_FAILED_TO_LOAD = 'FILE_FAILED_TO_LOAD',

  // Cognito
  CODE_MISMATCH_EXCEPTION = 'CODE_MISMATCH_EXCEPTION',
  EXPIRED_CODE_EXCEPTION = 'EXPIRED_CODE_EXCEPTION',
  INVALID_PASSWORD_EXCEPTION = 'INVALID_PASSWORD_EXCEPTION',
  LIMIT_EXCEEDED_EXCEPTION = 'LIMIT_EXCEEDED_EXCEPTION',
  TOO_MANY_FAILED_ATTEMPTS_EXCEPTION = 'TOO_MANY_FAILED_ATTEMPTS_EXCEPTION',
  TOO_MANY_REQUESTS_EXCEPTION = 'TOO_MANY_REQUESTS_EXCEPTION',
  USER_NOT_CONFIRMED_EXCEPTION = 'USER_NOT_CONFIRMED_EXCEPTION',
  USER_NOT_FOUND_EXCEPTION = 'USER_NOT_FOUND_EXCEPTION',

  // Bulletins
  BULLETIN_NOT_FOUND = 'BULLETIN_NOT_FOUND',

  // Impersonation
  IMPERSONATION_WRITE_ACCESS_DENIED = 'IMPERSONATION_WRITE_ACCESS_DENIED',
}

export enum ContractToTenantRole {
  NON_TENANT = 'non_tenant',
  REPLACEMENT_APARTMENT_TENANT = 'replacement_apartment_tenant',
  SECONDARY_TENANT = 'secondary_tenant',
  LEGAL_GUARDIAN = 'legal_guardian',
  SUBTENANT = 'subtenant',
  TENANT = 'tenant',
}

export enum SlotStatus {
  FREE = 'free',
  OWN = 'own',
  OWN_RECURRING = 'own_recurring',
  RESERVED = 'reserved',
}

// For contract contacts, which type they are
export enum ContactCategory {
  PROPERTY_MAINTENANCE = 'property_maintenance',
  CUSTOMER_SERVICE = 'customer_service',
  PROPERTY_MANAGEMENT = 'property_management',
  SALES_REPRESENTATIVE = 'sales_representative',
  PROPERTY_SECRETARY = 'property_secretary',
  RESIDENT_COMMITTEE = 'resident_committee',
  OTHER = 'other',
}

export enum DocumentType {
  PDF = 'pdf',
  LINK = 'link',
}

export enum ConfirmOperationType {
  RESIDENT_APP_ACCEPT_CONTRACT_TERMINATION = 'resident_app_accept_contract_termination',
  RESIDENT_APP_CONFIRM_CONTRACT_TERMINATION = 'resident_app_confirm_contract_termination',
  RESIDENT_APP_REJECT_CONTRACT_TERMINATION = 'resident_app_reject_contract_termination',
  RESIDENT_APP_ACCEPT_CONTRACT_MODIFICATION = 'resident_app_accept_contract_modification',
  RESIDENT_APP_CONFIRM_CONTRACT_MODIFICATION = 'resident_app_confirm_contract_modification',
  RESIDENT_APP_REJECT_CONTRACT_MODIFICATION = 'resident_app_reject_contract_modification',
}

export enum ContractTerminationConfirmAction {
  ACCEPT = 'accept',
  REJECT = 'reject',
}

export enum ContractOperation {
  TERMINATION = 'termination',
  MODIFICATION = 'modification',
}

export enum ContractChangesSection {
  TERMINATE_CONTRACT = 'terminate_contract',
  RESIDENT_CHANGES = 'resident_changes',
}
